import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  Checkbox,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import type { ChipTone } from '@/design';
import type { IInternalFieldOption } from '../../api/internal-field-contracts';
import type {
  IListAssignee,
  IListCell,
  IListField,
  IListFormattingRule,
  IListRow
} from '../../api/lists-contracts';
import { InternalFieldControl } from '../../components/field-controls';
import { internalMalformedHint, type InternalUiValue } from '../../internal-field-values';
import { evaluateFormatting } from '../../list-criteria';
import { formatListValue, formattingTone } from '../../lists';
import { rawOf, storedUi, type CellEditKind, type ICellDraft } from './editing';

/**
 * A list's cell, by its field's kind (SR-230, design
 * `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md` §2, §3.5 and §3.6) — read, or on
 * an editable column the control that stages a draft (`EditableCell`, over `editing.ts`).
 *
 * **The value is `formatListValue`'s** — `lists.ts` holds every format — and an absent key is the
 * empty cell, drawn as nothing: survey-api leaves out of `cells` a value nobody holds and a column
 * the caller may not see alike. Nothing in a cell is a link.
 *
 * **A view's formatting rules are evaluated here and nowhere else** (§3.8, `evaluateFormatting`):
 * a rule on a cell colours that cell, a rule with `row` the whole row, the first rule that matches
 * winning — the order a view keeps them in. A rule's tone is the status chips' palette by name
 * (`FORMATTING_TONES`); a migrated colour no tone names draws grey and the Legend says so.
 */

/** A tone as a cell or a row is washed in — the chips' palette, lighter, so the text stays read. */
const TONE_WASH: Record<ChipTone, string> = {
  neutral: 'bg-[color-mix(in_srgb,var(--veritant-royal-15)_45%,white)]',
  info: 'bg-[var(--color-slight-blue)] text-[var(--color-royal-blue)]',
  success: 'bg-[color-mix(in_srgb,var(--color-teal)_12%,white)] text-[var(--color-teal)]',
  attention:
    'bg-[color-mix(in_srgb,var(--color-yellow)_35%,white)] text-[var(--color-midnight-blue)]',
  danger: 'bg-[color-mix(in_srgb,var(--color-magenta)_12%,white)] text-[var(--color-deep-magenta)]',
  mock: ''
};

/** What a matching rule does to a cell or a row: its wash and whether it is bold. */
export interface IFormatting {
  tone: ChipTone;
  bold: boolean;
}

/** The classes a rule's look takes. */
export function formattingClass(look: Nullable<IFormatting>): string {
  if (look === null) return '';
  return cn(TONE_WASH[look.tone], look.bold && 'font-semibold');
}

/** The first of `rules` the row meets — the row's own (`row: true`) or a field's. */
function firstMatch(
  rules: readonly IListFormattingRule[],
  row: IListRow,
  kindOf: (code: string) => IListField['kind'] | undefined,
  keep: (rule: IListFormattingRule) => boolean
): Nullable<IFormatting> {
  for (const rule of rules) {
    if (!keep(rule) || !rule.field) continue;
    if (evaluateFormatting(rule, kindOf(rule.field), row.cells[rule.field]?.v)) {
      return { tone: formattingTone(rule.tone).tone, bold: rule.bold === true };
    }
  }
  return null;
}

/** A row's look, from the rules that colour a whole row. */
export function rowFormatting(
  rules: readonly IListFormattingRule[],
  row: IListRow,
  kindOf: (code: string) => IListField['kind'] | undefined
): Nullable<IFormatting> {
  return firstMatch(rules, row, kindOf, (rule) => rule.row === true);
}

/** A cell's look, from the rules on its field that colour the cell alone. */
export function cellFormatting(
  rules: readonly IListFormattingRule[],
  row: IListRow,
  field: string,
  kindOf: (code: string) => IListField['kind'] | undefined
): Nullable<IFormatting> {
  return firstMatch(rules, row, kindOf, (rule) => rule.row !== true && rule.field === field);
}

/** A cell's value in words, by its field's kind — `''` for a cell holding nothing. */
export function cellText(field: Pick<IListField, 'kind' | 'options'>, cell: IListCell | undefined): string {
  if (cell === undefined) return '';
  return formatListValue(field, cell.v, cell.label);
}

/** A long text drawn whole (SR-408's D6): its line breaks kept, a long word broken inside the
 *  frame rather than widening it. */
export const LONG_TEXT_WHOLE = 'whitespace-pre-wrap break-words [overflow-wrap:anywhere]';

/**
 * One cell of the grid, read: the value in words on one line (the frame scrolls sideways rather
 * than wrap), `data-cell` its field's code — a long text's line breaks drawn as spaces by the line's
 * `nowrap`, cut by the cell's width. `whole` (the reading pane) draws a long text on its lines.
 */
export function CellValue({
  field,
  cell,
  look,
  whole = false
}: {
  field: IListField;
  cell: IListCell | undefined;
  look: Nullable<IFormatting>;
  whole?: boolean;
}) {
  const text = cellText(field, cell);
  return (
    <span
      data-cell={field.code}
      data-empty={text === '' ? '' : undefined}
      className={cn(
        'block rounded-sm',
        whole && field.kind === 'long_text' ? LONG_TEXT_WHOLE : 'max-w-[24rem] truncate',
        look && '-mx-1 px-1',
        formattingClass(look)
      )}
    >
      {text}
      {cell?.src === 'salesforce' && <SourceMark />}
    </span>
  );
}

/** A value migrated from Salesforce (SR-457): a muted "SF" after it, `says` in its tooltip. */
export function SourceMark({ says = 'From Salesforce' }: { says?: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          tabIndex={0}
          aria-label={says}
          data-source="salesforce"
          className="ml-1.5 inline-flex items-center rounded border border-[color-mix(in_srgb,var(--color-sapphire)_25%,white)] bg-[color-mix(in_srgb,var(--color-sapphire)_10%,white)] px-1 py-px align-middle text-[0.625rem] leading-none font-semibold tracking-wide text-[var(--color-sapphire)] outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          SF
        </span>
      </TooltipTrigger>
      <TooltipContent>{says}</TooltipContent>
    </Tooltip>
  );
}

// ---- Editing --------------------------------------------------------------------------------

/** The select's "nobody" / "no value" item: a Radix item cannot carry `''`. */
const NONE = '__none__';

/** A list field's options as the staff-field controls take them — in the catalogue's order, which
 *  inlines the options a value may take. */
function controlOptions(field: IListField): IInternalFieldOption[] {
  return (field.options ?? []).map((option, ordinal) => ({
    code: option.code,
    label: option.label,
    ordinal,
    retired: false,
    meta: null
  }));
}

/**
 * One editable field's control, by how it is edited (§3.6): the staff field's own type through
 * the hub's controls (`InternalFieldControl` — a box typed for a keyboard, a date, a tick, a list of
 * ticks), and a choice — a select's options, or *Assigned to*'s accounts by email with "Nobody" —
 * as the kit's select, which a cell opens at once. Judges nothing: what it holds is staged by
 * `editing.ts`, which holds malformed input.
 */
export function ValueControl({
  kind,
  field,
  label,
  ui,
  onChange,
  accounts,
  open,
  onOpenChange
}: {
  kind: CellEditKind;
  field: IListField;
  /** The accessible name — "<field> for <name>". */
  label: string;
  ui: InternalUiValue;
  onChange: (ui: InternalUiValue) => void;
  accounts: readonly IListAssignee[];
  /** A choice's list, open — a cell opens it at once; a form leaves it to the reader. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  if (kind === 'long_text') {
    // A text's box that keeps line breaks (the owner's Q2, SR-408): one line high, Shift+Enter adding
    // a break — a plain input would flatten a stored note's breaks on its first edit. Grown to its
    // content — over a grid cell as well, down over the rows below, to `10rem` (the cell's frame).
    return (
      <Textarea
        aria-label={label}
        rows={1}
        autoComplete="off"
        value={typeof ui === 'string' ? ui : ''}
        onChange={(event) => onChange(event.target.value)}
        className="min-h-7 resize-none py-1 text-[0.8125rem] [field-sizing:content]"
      />
    );
  }
  if (kind === 'select' || kind === 'assignee') {
    const current = typeof ui === 'string' ? ui : '';
    const choices =
      kind === 'assignee'
        ? accounts.map((account) => ({ code: account.accountId, label: account.email }))
        : (field.options ?? []);
    // A stored value no longer offered stays drawn, so opening the control changes nothing.
    const stray = current !== '' && !choices.some((choice) => choice.code === current);
    return (
      <Select
        value={current === '' ? NONE : current}
        open={open}
        onOpenChange={onOpenChange}
        onValueChange={(next) => onChange(next === NONE ? '' : next)}
      >
        <SelectTrigger aria-label={label} className="h-7 w-full min-w-[9rem] text-[0.8125rem]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>{kind === 'assignee' ? 'Nobody' : '— None —'}</SelectItem>
          {stray && (
            <SelectItem value={current} disabled>
              {current} — not offered now
            </SelectItem>
          )}
          {choices.map((choice) => (
            <SelectItem key={choice.code} value={choice.code}>
              {choice.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  return (
    <InternalFieldControl
      field={{ type: kind, options: controlOptions(field) }}
      control={{ label, value: ui, onChange, disabled: false }}
    />
  );
}

/** What an editable cell knows of its edit: how, the draft, a refusal of it, the accounts. */
export interface ICellEdit {
  kind: CellEditKind;
  draft: ICellDraft | undefined;
  /** survey-api's 400 at this cell, in its words. */
  error: string | undefined;
  accounts: readonly IListAssignee[];
  /** Stages what the control holds — `badInput` a half-typed date. */
  onStage: (ui: InternalUiValue, badInput: boolean) => void;
}

/** A value in words as an editable cell draws it — the draft's where there is one. */
export function editedText(field: IListField, cell: IListCell | undefined, edit: ICellEdit): string {
  const { draft } = edit;
  if (draft === undefined) return cellText(field, cell);
  if (draft.staged.held) return typeof draft.ui === 'string' ? draft.ui : '';
  const raw = rawOf(draft.kind, draft.staged.value);
  const label =
    draft.kind === 'assignee' && typeof raw === 'string'
      ? edit.accounts.find((account) => account.accountId === raw)?.email
      : undefined;
  return formatListValue(field, raw, label);
}

/** Whether a date input holds a half-typed date — handed over as `''` like an emptied one. */
function halfTyped(box: Nullable<HTMLElement>): boolean {
  return box?.querySelector('input')?.validity.badInput === true;
}

/**
 * An editable cell (§3.6): the value, pending or not, and its control on a press — or, in the
 * reading pane's Edit (`form`), the control always. **A cell is a draft until Save data**: Enter
 * or leaving the control stages what it holds, Esc puts back what the press found and leaves the
 * control — never the page; a choice is staged as it is chosen and a tick as it is ticked. A
 * pending cell takes the Yellow left bar (`data-pending`); one holding text no payload can carry
 * says the shape it wants (`data-held`); a refusal of it is said under it.
 *
 * **A box or a select pressed open lies over its grid cell, the cell's whole width** — the grid's
 * cell is `relative` for it — with the value's words kept in place under it, unseen, so the column
 * keeps the width it had. In the flow, a box's own width was some twenty characters, narrower than
 * the value it opened on; held to the column's width, it took a larger share of the table's spare
 * width and widened the column under the press.
 */
export function EditableCell({
  field,
  row,
  name,
  edit,
  look,
  form = false
}: {
  field: IListField;
  row: IListRow;
  /** The row's person — the control's name, "<field> for <name>". */
  name: string;
  edit: ICellEdit;
  look?: Nullable<IFormatting>;
  form?: boolean;
}) {
  const { kind, draft, error } = edit;
  const cell = row.cells[field.code];
  const label = `${field.label} for ${name}`;
  const current = draft?.ui ?? storedUi(kind, cell);
  const [editing, setEditing] = React.useState(false);
  const [ui, setUi] = React.useState<InternalUiValue>(current);
  const box = React.useRef<HTMLSpanElement>(null);
  const text = editedText(field, cell, edit);

  // What the control starts from follows the draft and the row — a poll's patch, a save adopted —
  // while nobody is typing in it.
  const shown = JSON.stringify(current);
  React.useEffect(() => {
    if (!editing) setUi(current);
  }, [shown, editing]);

  React.useEffect(() => {
    if (editing && !form) box.current?.querySelector<HTMLElement>('input, textarea, button')?.focus();
  }, [editing, form]);

  const stage = (next: InternalUiValue, badInput = false) => edit.onStage(next, badInput);
  // What was typed, staged — and nothing where the control holds what it opened with: a stored
  // value the control cannot read opens empty, and leaving it untouched must not stage a clear.
  const stageTyped = () => {
    const badInput = halfTyped(box.current);
    if (badInput || JSON.stringify(ui) !== shown) stage(ui, badInput);
  };
  const close = () => setEditing(false);
  const commit = () => {
    stageTyped();
    close();
  };

  const frame = (children: React.ReactNode) => (
    <span
      ref={box}
      data-cell={field.code}
      data-pending={draft ? '' : undefined}
      data-empty={text === '' ? '' : undefined}
      className={cn(
        'block min-w-[4rem] rounded-sm',
        draft && '-ml-1.5 pl-1 shadow-[inset_3px_0_0_var(--color-yellow)]',
        formattingClass(look ?? null)
      )}
    >
      {children}
      {draft?.staged.held && kind !== 'assignee' && (
        <span data-held="" className="block text-xs whitespace-normal text-destructive">
          {internalMalformedHint(kind)}
        </span>
      )}
      {error && (
        <span data-cell-error="" className="block text-xs whitespace-normal text-destructive">
          {error}
        </span>
      )}
    </span>
  );

  if (kind === 'bool') {
    return frame(
      <Checkbox
        aria-label={label}
        checked={current === true}
        onCheckedChange={(next) => stage(next === true)}
      />
    );
  }

  const choice = kind === 'select' || kind === 'assignee' || kind === 'multi_select';
  const keys = (event: React.KeyboardEvent) => {
    // Shift+Enter in a long text is its line break; Enter stages, as in any box.
    if (event.key === 'Enter' && !(kind === 'long_text' && event.shiftKey)) {
      event.preventDefault();
      if (form) stageTyped();
      else commit();
    } else if (event.key === 'Escape' && !form) {
      // The control's Esc: put back what the press found, and stay on the page.
      event.preventDefault();
      event.stopPropagation();
      setUi(current);
      close();
    }
  };

  if (form) {
    if (choice) {
      return frame(
        <ValueControl
          kind={kind}
          field={field}
          label={label}
          ui={current}
          onChange={(next) => stage(next)}
          accounts={edit.accounts}
        />
      );
    }
    return frame(
      <span
        onKeyDown={keys}
        onFocus={() => setEditing(true)}
        onBlur={(event) => {
          if (event.currentTarget.contains(event.relatedTarget as Node)) return;
          setEditing(false);
          stageTyped();
        }}
        className="block"
      >
        <ValueControl kind={kind} field={field} label={label} ui={ui} onChange={setUi} accounts={edit.accounts} />
      </span>
    );
  }

  const button = (
    <button
      type="button"
      aria-label={label}
      onClick={() => setEditing(true)}
      className="block h-6 w-full max-w-[24rem] truncate rounded-sm px-0.5 text-left outline-none hover:bg-[var(--accent)] focus-visible:ring-2 focus-visible:ring-ring"
    >
      {text}
    </button>
  );
  // The stored value migrated from Salesforce is marked as `CellValue` marks it — beside the press,
  // never inside it; a staged change is the analyst's own.
  const face =
    cell?.src === 'salesforce' && draft === undefined ? (
      <span className="flex max-w-[24rem] items-center">
        <span className="min-w-0 flex-1">{button}</span>
        <SourceMark />
      </span>
    ) : (
      button
    );

  if (!editing) return frame(face);

  /** The control over the cell, the value's words holding the column's width under it. `z-[1]`:
   *  past a narrow cell it lies over the next one, which a later cell paints over otherwise and
   *  takes the presses from (the grid's sticky header is `z-[2]`). `h-7!`: the Team Portal's
   *  unlayered 2rem height of a box would run over a held or refused line under the value. A long
   *  text grows down over the rows below to show every line it holds (gate 2's owner ruling — one
   *  line high, a multi-line note looked like a one-liner a Ctrl+A would replace), scrolling past
   *  `10rem`. */
  const over = (control: React.ReactNode, props: React.HTMLAttributes<HTMLSpanElement> = {}) =>
    frame(
      <>
        <span aria-hidden className="invisible block h-6 max-w-[24rem] truncate px-0.5">
          {text}
        </span>
        <span
          {...props}
          className="absolute inset-x-1 top-0.5 z-[1] min-w-[8rem] [&_[data-slot=select-trigger]]:h-7! [&_input]:h-7! [&_textarea]:max-h-40 [&_textarea]:overflow-y-auto"
        >
          {control}
        </span>
      </>
    );

  if (kind === 'select' || kind === 'assignee') {
    return over(
      <ValueControl
        kind={kind}
        field={field}
        label={label}
        ui={current}
        open
        onOpenChange={(open) => {
          if (!open) close();
        }}
        onChange={(next) => {
          stage(next);
          close();
        }}
        accounts={edit.accounts}
      />
    );
  }

  if (kind === 'multi_select') {
    return frame(
      <Popover
        open
        onOpenChange={(open) => {
          if (!open) commit();
        }}
      >
        <PopoverAnchor asChild>{face}</PopoverAnchor>
        <PopoverContent
          align="start"
          aria-label={label}
          className="grid w-72 gap-2 p-3"
          onEscapeKeyDown={(event) => {
            // Esc puts back what the press found, as a box's does; any other close stages.
            event.preventDefault();
            setUi(current);
            close();
          }}
        >
          <ValueControl kind={kind} field={field} label={label} ui={ui} onChange={setUi} accounts={edit.accounts} />
          <Button type="button" size="sm" className="justify-self-end" onClick={commit}>
            Done
          </Button>
        </PopoverContent>
      </Popover>
    );
  }

  return over(
    <ValueControl kind={kind} field={field} label={label} ui={ui} onChange={setUi} accounts={edit.accounts} />,
    {
      onKeyDown: keys,
      onBlur: (event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node)) return;
        commit();
      }
    }
  );
}
